import { useState, useCallback } from 'react';
import { ReportsController } from '../core';

const useReports = () => {
  const [reportEvents, setReportEvents] = useState([]);
  const [selectedReportEventId, setSelectedReportEventId] = useState('');
  const [loadingReports, setLoadingReports] = useState(false);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [lastLoadedEventId, setLastLoadedEventId] = useState(null);

  const loadReportEvents = useCallback(async (email) => {
    setLoadingReports(true);
    setError(null);
    
    try {
      const allEvents = [];
      
      // 1. Cargar eventos validados de la BD (historial)
      try {
        const validatedResult = await ReportsController.getValidatedEvents();
        if (validatedResult.success && validatedResult.data.length > 0) {
          allEvents.push(...validatedResult.data);
        }
      } catch (error) {
        console.warn('No se pudieron cargar eventos validados:', error);
      }
      
      // 2. Cargar eventos actuales de Google Calendar
      try {
        const { EventsController } = await import('../core');
        const userEventsResult = await EventsController.getUserEvents(email);
        if (userEventsResult.success && userEventsResult.data.length > 0) {
          // Crear conjunto de eventos existentes usando múltiples criterios
          const existingEvents = new Set();
          allEvents.forEach(event => {
            // Crear clave única basada en título + fecha
            const title = (event.summary || event.title || event.nombre || '').toLowerCase().trim();
            const date = event.start?.dateTime || event.start?.date || event.fecha_inicio;
            const dateKey = date ? new Date(date).toISOString().split('T')[0] : '';
            const uniqueKey = `${title}|${dateKey}`;
            existingEvents.add(uniqueKey);
            
            // También agregar por ID si existe
            if (event.id) existingEvents.add(`id:${event.id}`);
            if (event.event_id) existingEvents.add(`id:${event.event_id}`);
          });
          
          // Filtrar eventos de calendar que no estén duplicados
          const newEvents = userEventsResult.data.filter(calendarEvent => {
            const title = (calendarEvent.summary || calendarEvent.title || '').toLowerCase().trim();
            const date = calendarEvent.start?.dateTime || calendarEvent.start?.date;
            const dateKey = date ? new Date(date).toISOString().split('T')[0] : '';
            const uniqueKey = `${title}|${dateKey}`;
            
            // Verificar si ya existe por título+fecha o por ID
            const existsByKey = existingEvents.has(uniqueKey);
            const existsById = calendarEvent.id && existingEvents.has(`id:${calendarEvent.id}`);
            
            return !existsByKey && !existsById;
          });
          
          allEvents.push(...newEvents);
        }
      } catch (error) {
        console.warn('No se pudieron cargar eventos del calendario:', error);
      }
      
      if (allEvents.length > 0) {
        // Ordenar por fecha (más recientes primero)
        const sortedEvents = allEvents.sort((a, b) => {
          const dateA = new Date(a.start?.dateTime || a.start?.date || a.fecha_inicio || 0);
          const dateB = new Date(b.start?.dateTime || b.start?.date || b.fecha_inicio || 0);
          return dateB - dateA;
        });
        setReportEvents(sortedEvents);
      } else {
        setError('No se encontraron eventos disponibles para generar reportes.');
      }
    } catch (error) {
      console.error('Error en loadReportEvents:', error);
      setError('Error al cargar eventos: ' + error.message);
    }
    
    setLoadingReports(false);
  }, []);



  const selectReportEvent = useCallback((eventId) => {
    setSelectedReportEventId(eventId);
    setError(null);
    setHtmlContent(''); // Limpiar contenido anterior
  }, []);

  const viewHTMLReport = useCallback(async (eventId, eventTitle = null) => {
    // Prevenir llamadas duplicadas consecutivas al mismo evento
    if (loadingReports || lastLoadedEventId === eventId) {
      return;
    }
    
    setLoadingReports(true);
    setError(null);
    setLastLoadedEventId(eventId);
    
    try {
      const result = await ReportsController.getHTMLReport(eventId, eventTitle);
      
      if (result.success) {
        setHtmlContent(result.data);
      } else {
        setError(result.error || 'Error al cargar el reporte HTML');
      }
    } catch (error) {
      console.error('Error en viewHTMLReport:', error);
      setError(error.message || 'Error al generar el reporte. Por favor, intenta nuevamente.');
    }
    
    setLoadingReports(false);
  }, [loadingReports, lastLoadedEventId]);

  const viewJSONReport = useCallback(async (eventId) => {
    setLoadingReports(true);
    setError(null);
    
    try {
      const result = await ReportsController.getJSONReport(eventId);
      
      if (result.success) {
        console.log('JSON Report:', result.data);
        // Aquí podrías mostrar el JSON en un modal o componente específico
      } else {
        setError(result.error || 'Error al cargar el reporte JSON');
      }
    } catch (error) {
      console.error('Error en viewJSONReport:', error);
      setError('Error al generar el reporte JSON. Por favor, intenta nuevamente.');
    }
    
    setLoadingReports(false);
  }, []);

  const downloadReport = useCallback(async (eventId, format, eventTitle = null) => {
    setLoadingReports(true);
    setError(null);
    
    try {
      const result = await ReportsController.downloadReport(eventId, format, eventTitle);
      
      if (!result.success) {
        setError(result.error || `Error al descargar el reporte en formato ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Error en downloadReport:', error);
      setError(error.message || `Error al descargar el reporte. Por favor, intenta nuevamente.`);
    }
    
    setLoadingReports(false);
  }, []);
  
  const downloadReportCustom = useCallback(async (customUrl, eventTitle, format) => {
    setLoadingReports(true);
    setError(null);
    
    try {
      const result = await ReportsController.downloadReportCustom(customUrl, eventTitle, format);
      
      if (!result.success) {
        setError(result.error || `Error al descargar el reporte en formato ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Error en downloadReportCustom:', error);
      setError(error.message || `Error al descargar el reporte. Por favor, intenta nuevamente.`);
    }
    
    setLoadingReports(false);
  }, []);

  const clearReports = useCallback(() => {
    setReportEvents([]);
    setSelectedReportEventId('');
    setHtmlContent('');
    setError(null);
    setLastLoadedEventId(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    reportEvents,
    selectedReportEventId,
    loadingReports,
    error,
    htmlContent,
    
    // Actions
    loadReportEvents,
    selectReportEvent,
    viewHTMLReport,
    viewJSONReport,
    downloadReport,
    downloadReportCustom,
    clearReports,
    clearError,
    setError
  };
};

export default useReports;